/**
 * US-E1-003 — Password recovery: запрос ссылки на email.
 * Backend: POST /api/v1/auth/recover { email } → 204 (всегда, no enumeration).
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { ApiError } from '@/api/client'
import { Icon } from '@/components/icons/Icon'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { TextInput } from '@/components/auth/TextInput'
import { Button } from '@/components/ui/Button'
import { Banner } from '@/components/ui/Banner'

export function Recover() {
  const [email, setEmail] = useState('')

  const mutation = useMutation({
    mutationFn: (e: string) => authApi.recover(e),
  })

  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!email) return
    mutation.mutate(email.trim())
  }

  if (mutation.isSuccess) {
    return (
      <AuthLayout side="login">
        <h1 className="text-3xl font-bold font-display text-text-primary mb-3 text-center lg:text-left">Проверь почту 📬</h1>
        <p className="text-text-secondary mb-6 text-center lg:text-left">
          Если адрес <span className="font-semibold text-text-primary">{email}</span> зарегистрирован,
          мы отправили ссылку для сброса пароля. Действует 1 час.
        </p>
        <p className="text-sm text-text-muted mb-8 text-center lg:text-left">
          Не пришло? Проверь папку «Спам». Через минуту можно запросить ещё раз.
        </p>
        <a href="/auth/login" className="inline-flex items-center gap-2 text-text-link font-medium">
          <Icon name="arrow-left" size={16} />
          Вернуться к входу
        </a>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout side="login">
      <h1 className="text-3xl font-bold font-display text-text-primary mb-2 text-center lg:text-left">Забыл пароль? 🔑</h1>
      <p className="text-text-secondary mb-10 text-center lg:text-left">
        Введи email — пришлём ссылку для сброса.
      </p>

      <form onSubmit={onSubmit} className="space-y-6">
        <div>
          <label htmlFor="recover-email" className="block text-sm font-semibold text-text-primary mb-2">Email</label>
          <TextInput
            id="recover-email"
            type="email"
            inputMode="email"
            required
            autoComplete="email"
            autoFocus
            value={email}
            onChange={setEmail}
            placeholder="alex@company.ru"
            leftSlot={<Icon name="mail" size={20} />}
            hasError={mutation.isError}
            aria-describedby={mutation.isError ? 'recover-email-error' : undefined}
          />
        </div>

        {mutation.isError && (
          <div id="recover-email-error">
            <Banner variant="error" body={mutation.error instanceof ApiError ? mutation.error.message : 'Что-то пошло не так. Попробуй ещё раз.'} />
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth loading={mutation.isPending} disabled={!email}>
          {mutation.isPending ? 'Отправляем…' : 'Отправить ссылку'}
        </Button>

        <div className="flex items-center justify-between pt-2">
          <a href="/auth/login" className="text-sm text-text-link font-medium">
            Вернуться к входу
          </a>
          <a href="/auth/register" className="text-sm text-text-link font-medium">
            Создать аккаунт
          </a>
        </div>
      </form>
    </AuthLayout>
  )
}
